/**
 * IconButton - Consistent icon-only button component
 *
 * @example
 * <IconButton
 *   icon={<EditIcon />}
 *   label="Edit"
 *   onClick={handleEdit}
 * />
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

export type IconButtonVariant = "default" | "ghost" | "primary" | "danger";
export type IconButtonSize = "xs" | "sm" | "md" | "lg";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon element */
  icon: ReactNode;
  /** Accessible label */
  label: string;
  /** Visual variant */
  variant?: IconButtonVariant;
  /** Button size */
  size?: IconButtonSize;
  /** Loading state */
  loading?: boolean;
}

const SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: "h-6 w-6 text-xs",
  sm: "h-8 w-8 text-sm",
  md: "h-9 w-9 text-base",
  lg: "h-11 w-11 text-lg",
};

const ICON_SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  default: `
    border border-slate-200 bg-white text-slate-600
    hover:bg-slate-50 hover:text-slate-800 hover:border-slate-300
    focus:ring-slate-300
    dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400
    dark:hover:bg-slate-700 dark:hover:text-slate-200
  `,
  ghost: `
    bg-transparent text-slate-500
    hover:bg-slate-100 hover:text-slate-700
    focus:ring-slate-300
    dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200
  `,
  primary: `
    bg-gradient-brand text-white
    hover:brightness-105 hover:shadow-md
    focus:ring-[#00B894]
  `,
  danger: `
    bg-transparent text-slate-500
    hover:bg-rose-50 hover:text-rose-600
    focus:ring-rose-300
    dark:hover:bg-rose-950/30 dark:hover:text-rose-400
  `,
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      label,
      variant = "default",
      size = "md",
      loading = false,
      disabled,
      className = "",
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || loading}
        aria-label={label}
        title={label}
        className={`
          inline-flex items-center justify-center rounded-lg
          shadow-sm transition
          focus:outline-none focus:ring-2 focus:ring-offset-1
          disabled:opacity-50 disabled:cursor-not-allowed
          ${SIZE_CLASSES[size]}
          ${VARIANT_CLASSES[variant]}
          ${className}
        `}
        {...props}
      >
        {loading ? (
          <span
            className={`animate-spin rounded-full border-2 border-current border-t-transparent ${ICON_SIZE_CLASSES[size]}`}
          />
        ) : (
          <span className={ICON_SIZE_CLASSES[size]}>{icon}</span>
        )}
      </button>
    );
  },
);

IconButton.displayName = "IconButton";

/** Pre-built icon buttons for common actions */

export function EditButton({
  onClick,
  size = "sm",
}: {
  onClick: () => void;
  size?: IconButtonSize;
}) {
  return (
    <IconButton
      icon={
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      }
      label="Edit"
      variant="ghost"
      size={size}
      onClick={onClick}
    />
  );
}

export function DeleteButton({
  onClick,
  size = "sm",
}: {
  onClick: () => void;
  size?: IconButtonSize;
}) {
  return (
    <IconButton
      icon={
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        </svg>
      }
      label="Delete"
      variant="danger"
      size={size}
      onClick={onClick}
    />
  );
}

export function ViewButton({
  onClick,
  size = "sm",
}: {
  onClick: () => void;
  size?: IconButtonSize;
}) {
  return (
    <IconButton
      icon={
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      }
      label="View"
      variant="ghost"
      size={size}
      onClick={onClick}
    />
  );
}

export default IconButton;
